/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';

import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import Spinner, { SpinnerSize } from '../Spinner';
import { FontWeight, QuicksandBold } from '../Text';
import Button from '../Button';

export const InputSize = {
  SMALL: 1,
  NORMAL: 2
};

const propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  multiple: PropTypes.bool,
};

const defaultProps = {
  type: 'file',
  placeholder: '',
  leftIcon: null,
  rightIcon: null,
  leftRender: null,
  rightRender: null,
  label: null,
  format: value => value,
  parse: value => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: value => value,
  onBlur: value => value,
  onFocus: value => value,
  onPressEnter: value => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  multiple: Boolean(false)
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};

  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

const inverseBlueInputMixin = css`
  background-color: transparent;
  color: ${Colors.WHITE};

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
  &:active:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
`;

const inverseBlueContainerMixin = css`
  background: none;
  border-color: ${Colors.WHITE};

  & input {
    ${inverseBlueInputMixin}

    &[disabled] {
      background: none;
    }
  }
`;

const smallInputMixin = css`
  height: 32px;
`;

// Components

export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;

  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};

  transition: box-shadow 0.15s ease;
  
  ${({ isFocused }) =>
    isFocused &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background2: ${Colors.GRAY[5]}
        background: ${Colors.BLUE[0]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}

  ${({ inline }) =>
    inline &&
    css`
      min-width: 115px;
      width: 80%;
    `}
  
  ${({ meta }) => (meta && meta.touched && meta.error ? errorInputMixin : '')}

  ${({ inverseBlue }) => inverseBlue && inverseBlueContainerMixin}
`;

export const Input = styled.input`
  width: ${({ smallInput }) => (smallInput ? '40px' : '100%')};
  min-width: 0;
  height: 48px;
  padding: 8px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 16px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${QuicksandBold}

  transition: background 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${Colors.GRAY[3]};
  }

  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }

  ${({ size }) => size && _getInputSizeMixin(size)}
`;

export const IconButton = styled(Button).attrs({
  type: 'button',
  tabIndex: -1
})`
  padding: 0;

  & ${Icon} {
    font-size: 16px;
    color: ${Colors.GRAY[3]};
    margin: 0 12px;
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  ${({ inline }) =>
    inline &&
    css`
      display: flex;
    `}

  ${({ disabled, inverse }) =>
    disabled &&
    inverse &&
    css`
      opacity: 0.3;
    `}
`;

const _getInputSizeMixin = size => {
  if (size === InputSize.SMALL) {
    return smallInputMixin;
  }

  return '';
};

const InputField = ({
  type,
  placeholder,
  disabled,
  readOnly,
  leftIcon,
  leftIconClick,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  classname,
  form: { setFieldValue, setFieldTouched, touched, errors },
  inputProps,
  validationMinHeight,
  hideLabelAsterisk,
  smallInput,
  alignCenter,
  inline,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  multiple,
  ...props
}) => {
  const inputRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (
      inputRef &&
      inputRef.current &&
      inputRef.current.selectionStart &&
      (selectionStart.current || selectionStart.current === 0)
    ) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  const meta = {
    touched: touched[field.name],
    error: errors[field.name],
    isFocused
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };
  return (
    <Wrapper
      inline={inline}
      disabled={disabled}
      inverse={inverseBlue}
      {...props}
    >
      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
        inline={inline}
        inverseBlue={inverseBlue}
      >
        {leftIcon && (
          <IconButton onClick={leftIconClick}>
            <Icon name={leftIcon} />
          </IconButton>
        )}
        {leftRender && leftRender()}
        <Input
          ref={ref => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          size={size}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          multiple={multiple}
          {...field}
          onKeyDown={e => {
            //if (e.key.toLowerCase() === 'enter') {
            //  onPressEnter(field.value);
            //}
          }}
          onFocus={() => {
            //onFocus(field.value);
            //_onFocus();
          }}
          onBlur={() => {
            //onBlur(field.value);
            //_onBlur();

            //if (!meta.touched) {
            // setFieldTouched(field.name, true);
            //}
          }}
          value_OLD={
            field.value !== undefined && field.value !== null
              ? format(field.value)
              : ''
          }
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          onChange={e => {

            /*
            
            let currentSelection = e.target.selectionStart;
            const event = { ...e };
            const origValue = e.target.value;
            const maskedPrevValue = format(field.value) || '';

            // Note - we call format function to clear the input value
            // to remove exceeded characters
            //if (type!=="file"){
              event.target.value = parse(format(origValue));
            //}
            const maskedValue = format(event.target.value);

            const diffCaretPosistion =
              maskedValue.length - maskedPrevValue.length;
            currentSelection += diffCaretPosistion > 1 ? diffCaretPosistion : 0;

            // Temporary comment because it doesn't changes the value based on the mask
            // field.onChange(event);
            //if (type!=="file"){
              setFieldValue(field.name, parse(format(origValue)));
            //} else {
            //  setFieldValue(field.name, event.currentTarget.files[0]);
            //}

            onChange(event.target.value);
            selectionStart.current = currentSelection;

            */
            
          }}
          {...fieldProps}
        />
        {rightIcon && (
          <IconButton onClick={rightIconClick}>
            <Icon name={rightIcon} />
          </IconButton>
        )}
        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}
      </InputContainer>
      {!inline && (
        <ValidationError
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default InputField;
